/* eslint-disable @typescript-eslint/restrict-template-expressions */
import {
  Box,
  Flex,
  Heading,
  HStack,
  ListItem,
  Tag,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { HiOutlineSparkles } from "react-icons/hi";

import { AtsIcon, BhLogo, Button, Link } from "../../../components";
import { GreenhouseRatingsDisplay } from "../../../components/Greenhouse/GreenhouseRatingsDisplay";
import BhLinkedIn from "../../../components/Icons/BhLinkedIn";
import { summarizeGreenhouseRatings } from "../../../utils/greenhouse";
import MonospacedText from "../../components/MonospacedText";
import { CandidateLinkType, CandidateRediscoveryProspect } from "../../graphql";

interface BulletCitationsProps {
  citations?: string[] | null;
}

export function BulletCitations({
  citations,
}: BulletCitationsProps): JSX.Element | null {
  if (!citations || citations.length === 0) return null;
  return (
    <>
      {citations.map((citation, idx) => (
        <Link
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          fontWeight="normal"
          href={citation}
          target="_blank"
          rel="noopener noreferrer"
        >
          <MonospacedText
            display="inline-block"
            verticalAlign="super"
            fontSize="0.6rem"
            text={(idx + 1).toString()}
            marginRight="0.25rem"
          />
        </Link>
      ))}
    </>
  );
}

interface InfoBoxProps {
  label: string;
  children: React.ReactNode;
}

const InfoBox: React.FC<InfoBoxProps> = ({ label, children }) => (
  <VStack
    alignItems="flex-start"
    spacing={0}
    width="100%"
    minHeight="20px"
    fontSize="12px"
  >
    <Text color="gray.600">{label}:</Text>
    <Box color="gray.800">{children}</Box>
  </VStack>
);

interface ProspectCardProps {
  index: number;
  prospect: CandidateRediscoveryProspect;
}

interface ProspectCardProps {
  index: number;
  prospect: CandidateRediscoveryProspect;
  onOpenEmailModal: (prospect: CandidateRediscoveryProspect) => void;
}

const ProspectCard: React.FC<ProspectCardProps> = ({
  index,
  prospect,
  onOpenEmailModal,
}) => {
  const formatDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const linkedIn = prospect.candidate?.candidateLinks?.find(
    (item) => item && item.type === CandidateLinkType.Linkedin
  );

  const ratingDisplay = summarizeGreenhouseRatings(prospect.ratings);
  return (
    <Box maxWidth="886px">
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        color="white"
        bgColor="blue.700"
        borderTopLeftRadius="12px"
        borderTopRightRadius="12px"
        border="1px solid"
        borderColor="blue.700"
        px={4}
        py={2.5}
      >
        <Flex flexDirection="row" alignItems="center">
          <Heading fontWeight="600" fontSize="xl" my={0} pr={3}>
            {prospect.candidate.isRbacVisibleForCurrentUser ? (
              <Link
                variant="white"
                target="_blank"
                href={`/candidate/${prospect.candidateId}`}
              >
                {prospect.candidate.fullName}
              </Link>
            ) : (
              prospect.candidate.fullName
            )}
          </Heading>
          <Link href={`/candidate/${prospect.candidateId}`} variant="white">
            <BhLogo width="24px" height="24px" />
          </Link>
          {prospect.candidate.greenhouseLink && (
            <Link
              href={prospect.candidate.greenhouseLink}
              isExternal
              position="relative"
              top="-1px"
              ml={4}
            >
              <AtsIcon fill="white" width="auto" height="20px" isGreenhouse />
            </Link>
          )}
          {linkedIn && (
            <Link
              href={linkedIn.link}
              isExternal
              width={6}
              height={6}
              position="relative"
              top="-2px"
              ml={4}
            >
              <BhLinkedIn color="white" boxSize={5} />
            </Link>
          )}
        </Flex>
        <Button
          aria-label="Generate re-engagement message"
          leftIcon={<HiOutlineSparkles size={16} />}
          iconSpacing={1}
          onClick={() => onOpenEmailModal(prospect)}
          variant="whiteOutline"
          fontWeight="500"
          size="xs"
          borderRadius="4px"
          _hover={{
            backgroundColor: "whiteAlpha.100",
          }}
        >
          Reconnect
        </Button>
      </Flex>

      <Box
        borderColor="gray.200"
        borderWidth="1px"
        borderTopWidth="0"
        borderBottomLeftRadius="12px"
        borderBottomRightRadius="12px"
        p={6}
      >
        <HStack
          alignItems="flex-start"
          pb={6}
          borderBottom="1px solid"
          borderBottomColor="gray.200"
          spacing={0}
        >
          {/* TLDR Section */}
          <VStack
            color="gray.800"
            fontSize="sm"
            maxWidth="548px"
            spacing={2}
            pr={4}
            alignItems="flex-start"
          >
            <Text fontWeight="600">Highlights</Text>
            <Text>{prospect.output.tldr}</Text>
          </VStack>

          {/* Application Info */}
          {prospect.lastApplication && (
            <VStack
              spacing={2}
              color="gray.600"
              fontWeight="400"
              fontSize="sm"
              minWidth="254px"
              pl={6}
              borderLeft="1px solid"
              borderLeftColor="gray.200"
            >
              {prospect.lastApplication.candidateLastApplicationDate && (
                <InfoBox label="Last applied date">
                  {formatDate(
                    prospect.lastApplication.candidateLastApplicationDate
                  )}
                </InfoBox>
              )}
              {prospect.lastApplication.position.title && (
                <InfoBox label="Last applied position">
                  {prospect.lastApplication.isRbacVisibleForCurrentUser ? (
                    <Link
                      href={`/position/${prospect.lastApplication.position.id}`}
                    >
                      {prospect.lastApplication.position.title}
                    </Link>
                  ) : (
                    prospect.lastApplication.position.title
                  )}
                </InfoBox>
              )}
              {prospect.lastApplication.atsStageName && (
                <InfoBox label="Last interview stage">
                  {prospect.lastApplication.atsStageName}
                </InfoBox>
              )}
              <InfoBox label="Rejection reason">
                {prospect.lastApplication.atsStatusReason || "Not provided"}
              </InfoBox>
              {!!prospect.ratings?.length && (
                <InfoBox label="Scorecard ratings">
                  {prospect.candidate.greenhouseLink ? (
                    <Link
                      variant="unstyled"
                      href={prospect.candidate.greenhouseLink}
                      isExternal
                    >
                      <GreenhouseRatingsDisplay ratings={ratingDisplay} />
                    </Link>
                  ) : (
                    <GreenhouseRatingsDisplay ratings={ratingDisplay} />
                  )}
                </InfoBox>
              )}
            </VStack>
          )}
        </HStack>
        {/* Categories */}
        <VStack align="stretch" spacing={6} pt={6}>
          {prospect.output?.categories?.map((category) => (
            <HStack
              key={`category-${category?.categoryName}-${prospect.candidateId}`}
              alignItems="flex-start"
              spacing={0}
              pb={6}
              borderBottom="1px solid"
              borderBottomColor="gray.200"
              _last={{
                borderBottom: "none",
                pb: 0,
              }}
            >
              <VStack maxWidth="548px" pr={2} alignItems="flex-start">
                <Text color="gray.800" fontSize="sm" fontWeight={600}>
                  {category?.tldr}
                </Text>
                <UnorderedList
                  color="gray.600"
                  fontSize="sm"
                  fontWeight={400}
                  lineHeight="1.5"
                  pl={3}
                >
                  {category?.bullets?.map((item, bulletIdx) => (
                    <ListItem
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${category?.categoryName}-${prospect.candidateId}-bullet-${bulletIdx}`}
                      fontSize="sm"
                    >
                      {item?.bullet}
                      {item?.transcriptCitations?.length ? (
                        <BulletCitations
                          citations={item?.transcriptCitations}
                        />
                      ) : null}
                    </ListItem>
                  ))}
                </UnorderedList>
              </VStack>
              <Flex flex={1} pl={6}>
                <Tag
                  size="sm"
                  color="gray.700"
                  fontWeight="400"
                  bgColor="gray.100"
                  borderRadius="4px"
                >
                  {category?.categoryName}
                </Tag>
              </Flex>
            </HStack>
          ))}
        </VStack>
      </Box>
    </Box>
  );
};

export default ProspectCard;
